import {
  Appointments,
  Businesses,
  Discounts,
  PaymentLineItems,
  PaymentMethod,
  Payments,
  PaymentStatus,
  PaymentType,
  ProviderCommissionType,
  SplitPaymentsGroups,
} from '@prisma/client';
import { capitalize } from '@mui/material';
import { Payment, PaymentMetadata } from '../../types/reports';
import {
  Appointment,
  Business,
  CheckoutFailureData,
  Customer,
  LoadedUser,
  Transaction,
} from '../../interfaces';
import {
  InitiatePaymentPayload,
  LineItem,
  PaymentsCapabilities,
  PaymentsGroup,
  PaymentsSplit,
  PaymentWithTransfers,
  PaymentLineItemWithService,
  PaymentWithTransfersAndLineItems,
  ProviderCommissionOption,
  SplitPaymentForm,
} from '../../types/payments';
import {
  DEFAULT_CUSTOMER_SERVICE_FEE,
  DEFAULT_PER_CHARGE_CENTS_FEES,
} from '../../constants/global';
import { OrderWithItems, OrderWithPayments } from '../../types/orders';
import {
  convertSnakeToTitleCase,
  formatDollarAmount,
  isNil,
} from '../../utils/global/helper';
import {
  CheckoutFormValues,
  CheckoutPaymentMethod,
} from '../../types/appointments';
import { getUserNameFromBusiness } from '../global';
import { isAppointmentDepositPaid } from '../appointments';
import { SplitPaymentMethod } from '../../context/SplitPaymentsContext/context';
import { FailureData } from '../../hooks/payments/useCheckoutSocket';
import {
  TERMINAL_MIN_FEE_CENTS,
  ONLINE_MIN_FEE_CENTS,
  EXCLUDED_SUM_LINE_ITEM_TYPES,
  DISCOUNT_LINE_ITEM_TYPES,
  LINE_ITEMS_ORDER,
  CREDIT_PAYMENT_METHODS,
} from '../../constants/payments';
import { getOrderItemName } from '../orders';
import { TATTOO_LABEL } from '../../constants/appointments';

const PAID_STATUSES = ['SUCCESS', 'REFUNDED', 'VOIDED'] as PaymentStatus[];

export const CASH_DISCOUNT_APPLIED_LABEL = 'Cash Discount Applied';

export function isCashDiscountEnabled(
  business: Pick<Businesses | Business, 'cashDiscountEnabled'>,
) {
  return !!business?.cashDiscountEnabled;
}

function getRetailLineItemLabel(
  orderItem: OrderWithItems['orderItems'][number],
): string {
  const name = getOrderItemName(orderItem) || 'Custom Item';
  return `${name}${orderItem.quantity > 1 ? `  (${orderItem.quantity})` : ''}`;
}

/**
 * Allocates a total retail discount across order items using the same order and
 * logic as the API (nontaxable items first, then by tax rate). Used to infer
 * per-item discount when we only have RETAIL_DISCOUNT on a payment.
 * Returns a map of orderItemId -> discount amount in cents for that item.
 */
export function allocateRetailDiscountToOrderItems(
  order: OrderWithItems,
  retailDiscountCents: number,
): Record<number, number> {
  const result: Record<number, number> = {};
  let remainingCents = retailDiscountCents;
  if (remainingCents <= 0 || !order?.orderItems?.length) return result;

  // Match API order: by tax rate (nontaxable first), then by orderItemId so
  // equal tax rates get a stable order consistent with the API.
  const sorted = [...order.orderItems].sort((a, b) => {
    const taxDiff = (a.taxRate ?? 0) - (b.taxRate ?? 0);
    if (taxDiff !== 0) return taxDiff;
    return a.orderItemId - b.orderItemId;
  });

  sorted.forEach(orderItem => {
    if (remainingCents <= 0) return;
    const price = orderItem.customItemPrice ?? orderItem.item?.price ?? 0;
    const originalTotalCents = Math.round(price * 100) * orderItem.quantity;
    const discountForItem = Math.min(remainingCents, originalTotalCents);
    remainingCents -= discountForItem;
    if (discountForItem > 0 && orderItem.orderItemId != null) {
      result[orderItem.orderItemId] = discountForItem;
    }
  });
  return result;
}

export function isPaymentComplete(payment: Pick<Payment, 'status'>) {
  return PAID_STATUSES.includes(payment.status);
}

export function isFreeCheckout(payments: Pick<Payment, 'amount'>[]) {
  return (
    payments.length > 0 && payments.reduce((sum, p) => sum + p.amount, 0) === 0
  );
}

export function getMetadataFromPayment(
  payment: Pick<Payments | Payment, 'metadata'>,
): PaymentMetadata {
  if (!payment?.metadata) return {};
  const metadata = payment.metadata || {};
  if (typeof metadata === 'string') {
    return JSON.parse(metadata) as PaymentMetadata;
  }
  return metadata as PaymentMetadata;
}

export function getPaymentAmountWithoutServiceFee(
  payment: Pick<Payments | Payment, 'amount' | 'metadata'>,
) {
  let { amount } = payment;
  const metadata = getMetadataFromPayment(payment);
  if (metadata.service_fee) {
    amount -= parseInt(String(metadata.service_fee), 10);
  }
  amount /= 100.0;
  return amount;
}

// Effective tax rate the payment was charged at, inferred from its own
// SERVICE_TAX/RETAIL_ITEM_TAX vs SERVICE/RETAIL_ITEM line items. Rate-stable
// across later shop-rate changes.
export function getPaymentEffectiveTaxRate(payment: {
  lineItems?: Pick<PaymentLineItems, 'type' | 'amount'>[];
}) {
  let base = 0;
  let tax = 0;
  payment.lineItems?.forEach(li => {
    if (li.type === 'SERVICE' || li.type === 'RETAIL_ITEM') base += li.amount;
    if (li.type === 'SERVICE_TAX' || li.type === 'RETAIL_ITEM_TAX')
      tax += li.amount;
  });
  return base > 0 ? tax / base : 0;
}

// Bundled tax-on-fee stashed inside legacy `FEE.amount`. CD payments store
// tax-on-fee separately (SERVICE_TAX.feeAmount) so this returns 0 for them.
export function getPaymentBundledTaxOnFeeCents(payment: {
  cashDiscountEnabled: boolean;
  lineItems?: Pick<PaymentLineItems, 'type' | 'amount'>[];
}) {
  if (payment.cashDiscountEnabled) return 0;
  const feeAmount =
    payment.lineItems?.reduce(
      (acc, li) => (li.type === 'FEE' ? acc + li.amount : acc),
      0,
    ) ?? 0;
  if (feeAmount === 0) return 0;
  const effRate = getPaymentEffectiveTaxRate(payment);
  if (effRate <= 0) return 0;
  const feeOnly = Math.round(feeAmount / (1 + effRate));
  return Math.max(0, feeAmount - feeOnly);
}

// All-in (with fee) for CD payments; merchant-net otherwise. A legacy non-CD
// payment viewed under a now-CD business also renders all-in — with the
// bundled tax-on-fee stripped so the number matches per-line breakdowns.
export function getPaymentDisplayAmount(
  payment: Pick<
    Payments | Payment,
    'amount' | 'metadata' | 'cashDiscountEnabled'
  > & {
    lineItems?: Pick<PaymentLineItems, 'type' | 'amount'>[];
  },
  businessCashDiscountEnabled = false,
) {
  if (payment.cashDiscountEnabled || businessCashDiscountEnabled) {
    return (payment.amount - getPaymentBundledTaxOnFeeCents(payment)) / 100;
  }
  return getPaymentAmountWithoutServiceFee(payment);
}

/**
 * A single split-payment's share of `splitPaymentsGroup.totalAmountCents`.
 * Summing this across payments gives the amount consumed from the group's
 * (inflated under CD, deflated otherwise) total — so `total - sum` is the
 * customer-facing remaining. Under CD, a feeless payment's share includes
 * the waived cash-discount cents.
 */
export function getSplitPaymentShareAmount(
  payment: Pick<
    Payments | Payment,
    'amount' | 'metadata' | 'cashDiscountEnabled'
  >,
) {
  if (payment.cashDiscountEnabled) {
    const metadata = getMetadataFromPayment(payment);
    const cashDiscountCents = Number(metadata.cash_discount_cents ?? 0);
    const waived = Number.isFinite(cashDiscountCents) ? cashDiscountCents : 0;
    return (payment.amount + waived) / 100;
  }
  return getPaymentAmountWithoutServiceFee(payment);
}

// Fee-only slice on the deposit, with any bundled tax-on-fee stripped out.
// CD payments store FEE.amount as fee only already; legacy pre-CD payments
// bundled fee + tax-on-fee — we decompose using the payment's own tax rate.
export function getDepositFeeOnlyCents(
  payment: Pick<Payments | Payment, 'metadata' | 'cashDiscountEnabled'> & {
    lineItems?: Pick<PaymentLineItems, 'type' | 'amount'>[];
  },
) {
  const feeAmount =
    payment.lineItems?.reduce(
      (acc, li) => (li.type === 'FEE' ? acc + li.amount : acc),
      0,
    ) ?? 0;
  if (feeAmount > 0) {
    if (payment.cashDiscountEnabled) return feeAmount;
    const effRate = getPaymentEffectiveTaxRate(payment);
    return effRate > 0 ? Math.round(feeAmount / (1 + effRate)) : feeAmount;
  }
  const metadata = getMetadataFromPayment(payment);
  const cashDiscountCents = Number(metadata.cash_discount_cents ?? 0);
  return Number.isFinite(cashDiscountCents) ? cashDiscountCents : 0;
}

// Deposit base + fee-only, no tax. Prefers line items; falls back to
// amount − tax. Legacy pre-CD payments bundle fee + tax-on-fee into FEE.amount,
// so we decompose using the payment's own effective tax rate.
export function getDepositPaidExclTaxAmount(
  payment: Pick<
    Payments | Payment,
    'amount' | 'metadata' | 'cashDiscountEnabled'
  > & {
    lineItems?: Pick<PaymentLineItems, 'type' | 'amount'>[];
  },
) {
  if (payment.lineItems?.length) {
    const baseCents = payment.lineItems.reduce(
      (acc, li) => (li.type === 'SERVICE' ? acc + li.amount : acc),
      0,
    );
    return (baseCents + getDepositFeeOnlyCents(payment)) / 100;
  }
  const metadata = getMetadataFromPayment(payment);
  const salesTaxCents = Number(metadata.sales_tax ?? 0);
  return (
    (payment.amount - (Number.isFinite(salesTaxCents) ? salesTaxCents : 0)) /
    100
  );
}

// Locked-in deposit base in cents from the SERVICE line item.
export function getDepositBaseCents(payment: {
  lineItems?: Pick<PaymentLineItems, 'type' | 'amount'>[];
}) {
  return payment.lineItems?.find(li => li.type === 'SERVICE')?.amount;
}

// All tax paid on this payment (deflated tax + any tax-on-fee stamped on
// SERVICE_TAX/RETAIL_ITEM_TAX.feeAmount for CD payments). Rate-stable vs
// `metadata.sales_tax`, which can drift for legacy payments.
export function getPaymentTaxPaidCents(payment: {
  lineItems?: Pick<PaymentLineItems, 'type' | 'amount' | 'feeAmount'>[];
}) {
  return (
    payment.lineItems?.reduce(
      (acc, li) =>
        li.type === 'SERVICE_TAX' || li.type === 'RETAIL_ITEM_TAX'
          ? acc + li.amount + (li.feeAmount ?? 0)
          : acc,
      0,
    ) ?? 0
  );
}

// Total saved bundled fee across feeless CD payments, in dollars.
export function getCashDiscountAppliedAmount(
  payments: Pick<Payments | Payment, 'metadata' | 'cashDiscountEnabled'>[],
) {
  return (
    payments.reduce((acc, payment) => {
      if (!payment.cashDiscountEnabled) return acc;
      const metadata = getMetadataFromPayment(payment);
      const cents = Number(metadata.cash_discount_cents ?? 0);
      return acc + (Number.isFinite(cents) ? cents : 0);
    }, 0) / 100
  );
}

// Total saved tax-on-fee across feeless CD payments, in dollars. Populated
// on feeless payments' metadata at finalize alongside cash_discount_cents.
export function getSalesTaxCashDiscountAmount(
  payments: Pick<Payments | Payment, 'metadata' | 'cashDiscountEnabled'>[],
) {
  return (
    payments.reduce((acc, payment) => {
      if (!payment.cashDiscountEnabled) return acc;
      const metadata = getMetadataFromPayment(payment);
      const cents = Number(metadata.sales_tax_cash_discount_cents ?? 0);
      return acc + (Number.isFinite(cents) ? cents : 0);
    }, 0) / 100
  );
}

export function getPaymentAmountWithoutServiceFeeOrSalesTax(
  payment: Pick<Payments | Payment, 'amount' | 'metadata'>,
) {
  const paymentWithoutFee = getPaymentAmountWithoutServiceFee(payment);
  const metadata = getMetadataFromPayment(payment);
  if (metadata?.sales_tax) {
    return paymentWithoutFee - (metadata.sales_tax || 0) / 100;
  }
  return paymentWithoutFee;
}

export function convertPaymentsToTransactions(
  payments: Payments[],
  extra: {
    appointment?: Appointment | null;
    order?: OrderWithPayments | null;
    paymentsGroup?: PaymentsGroup | null;
    customer?: Customer;
    businessCashDiscountEnabled?: boolean;
  } = {},
) {
  const cleanedTransactions = payments.map(payment => {
    const metadata = getMetadataFromPayment(payment);
    let amount = getPaymentDisplayAmount(
      payment,
      extra.businessCashDiscountEnabled,
    );

    // Convert stripe UNIX timestamp to date
    const dateCreated = new Date(payment.completedAt || payment.updatedAt);
    const dateUpdated = new Date(
      payment.updatedAt || payment.completedAt || payment.createdAt,
    );

    let tipAmt = metadata.tip_amt ? metadata.tip_amt / 100 : 0;

    if (metadata?.tip_amt) {
      tipAmt = metadata.tip_amt / 100;
      if (!isPaymentComplete(payment)) {
        amount -= tipAmt;
      }
    }

    return {
      id: payment.id,
      amount,
      dateCreated,
      dateUpdated,
      type: payment.method,
      tipAmount: tipAmt,
      chargeReason: metadata?.charge_reason,
      status: payment.status,
      error: payment.error || undefined,
      payment,
      discountAmount: (payment.discountAmount || 0) / 100,
      ...extra,
    } as Transaction;
  });
  return [...cleanedTransactions].sort(
    (a, b) => b.dateCreated.valueOf() - a.dateCreated.valueOf(),
  );
}

export function isCardPaymentMethod(
  paymentMethod: PaymentMethod | CheckoutPaymentMethod | undefined | null,
) {
  return (
    !!paymentMethod && ['CREDIT', 'TERMINAL', 'MANUAL'].includes(paymentMethod)
  );
}

export function isPaymentMethodSubjectToFees(
  paymentMethod: PaymentMethod | CheckoutPaymentMethod | undefined | null,
) {
  return (
    !!paymentMethod && ['CREDIT', 'TERMINAL', 'MANUAL'].includes(paymentMethod)
  );
}

export function getPaymentSplitsFromPayments(payments: PaymentWithTransfers[]) {
  const successPayments = payments.filter(
    payment => isPaymentComplete(payment) && !!payment.transfers?.length,
  );
  const paymentsSplit = successPayments.reduce<PaymentsSplit[]>(
    (list, payment) => {
      const currentPaymentList: PaymentsSplit[] = [];
      let paymentAmount = getPaymentDisplayAmount(payment);
      let transferTotal =
        payment.transfers?.reduce(
          (acc, transfer) => acc + transfer.amount / 100,
          0,
        ) ?? 0;

      const metadata = getMetadataFromPayment(payment);
      const tipAmount = metadata.tip_amt ? metadata.tip_amt / 100 : 0;

      if (tipAmount) {
        // Under CD, fees are bundled into the principal and never taken from
        // the tip — tip transfer = tip amount.
        let tipTransferAmount = tipAmount;
        if (!payment.cashDiscountEnabled) {
          const perChargeFeeCents =
            metadata.per_charge_fee_cents ?? DEFAULT_PER_CHARGE_CENTS_FEES;
          const perChargeFee = perChargeFeeCents / 100;
          const feesPaidFromPercentage =
            paymentAmount - transferTotal - perChargeFee;
          const feePercentage = feesPaidFromPercentage / paymentAmount;
          const tipFees = tipAmount * feePercentage;
          tipTransferAmount = tipAmount - tipFees;
        }

        paymentAmount -= tipAmount;
        transferTotal -= tipTransferAmount;

        currentPaymentList.push({
          id: payment.id,
          type: 'tip',
          amount: tipAmount,
          transfersAmount: tipTransferAmount,
        });
      }

      currentPaymentList.unshift({
        id: payment.id,
        type: 'payment',
        amount: paymentAmount,
        transfersAmount: transferTotal,
      });

      return [...list, ...currentPaymentList];
    },
    [],
  );

  return paymentsSplit;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function getTransactionCurrency(_transaction: Transaction) {
  return 'USD';
}

export function getPaymentMaxRefundAmount(
  payment: Payments | null | undefined,
) {
  if (!payment) return 0;

  const metadata = getMetadataFromPayment(payment);
  if (isNil(metadata.service_fee)) {
    return payment.amount / 100;
  }
  return getPaymentAmountWithoutServiceFee(payment);
}

export function isTipAllowed(paymentMethod: CheckoutPaymentMethod) {
  return ['CASH', 'DIGITAL', 'MANUAL', 'GIFT_CARD'].includes(paymentMethod);
}

export function isDiscountAllowed(paymentMethod: CheckoutPaymentMethod) {
  return [
    'CASH',
    'DIGITAL',
    'MANUAL',
    'TERMINAL',
    'GIFT_CARD',
    'FREE',
  ].includes(paymentMethod);
}

export function isFreeWithDiscount(
  paymentMethod: CheckoutPaymentMethod,
  discount: Discounts | null | undefined,
): boolean {
  return paymentMethod === 'FREE' && !!discount;
}

// GIFT_CARD last
export function sortPaymentsByPaymentMethod(payments: (Payments | Payment)[]) {
  return payments.sort((a, b) => {
    if (a.method === 'GIFT_CARD') return 1;
    if (b.method === 'GIFT_CARD') return -1;
    return 0;
  });
}

export function getPaymentWithMetadata(
  payments: Pick<Payments | Payment, 'type' | 'metadata'>[],
  type?: PaymentType,
) {
  const foundPayment = payments.find(
    payment =>
      (!type || payment.type === type) &&
      !!Object.keys(getMetadataFromPayment(payment))?.length,
  );
  if (!foundPayment) return null;

  return {
    ...foundPayment,
    metadata: getMetadataFromPayment(foundPayment),
  } as Payment;
}

export function getProcessedByName(user: LoadedUser, transaction: Transaction) {
  const { customer } =
    transaction.appointment ||
    transaction.order ||
    transaction.payment ||
    transaction;

  const tempCustomerId =
    !!customer && 'customerId' in customer
      ? customer.customerId
      : customer?.userId;

  const isProcessedByCustomer =
    tempCustomerId === transaction.payment?.processedByUserId;

  const userNameToShow = getUserNameFromBusiness(
    user,
    isProcessedByCustomer,
    transaction.payment?.processedByUserId,
  );

  return userNameToShow;
}

export function isGuestCheckout({
  customer,
  appointment,
}: {
  customer:
    | Pick<
        CheckoutFormValues['customer'],
        'customerId' | 'firstName' | 'phoneNumber'
      >
    | undefined;
  appointment: unknown;
}) {
  if (appointment) return false;
  // The phone input rewrites a cleared value back to the bare country dial
  // code (e.g. "1"), so a dial-code-only value still counts as empty.
  const phoneDigits = (customer?.phoneNumber ?? '').replace(/\D/g, '');
  return (
    !customer?.customerId && !customer?.firstName && phoneDigits.length <= 1
  );
}

export function getInitiatePaymentPayload({
  appointmentId,
  businessId,
  discount,
  values,
  failureData,
  guest,
}: {
  appointmentId?: string | null;
  businessId: string;
  discount?: Discounts | null;
  values: CheckoutFormValues;
  failureData?: CheckoutFailureData | null;
  guest?: boolean;
}): {
  data: InitiatePaymentPayload;
  overridePaymentMethod: PaymentMethod | undefined;
} {
  let overridePaymentMethod: PaymentMethod | undefined;

  const data: InitiatePaymentPayload = {
    businessId,
    customerId: guest ? null : values.customer.customerId,
    isCashTaxed: values.isCashTaxed,
    isManualCheckout: false,
    appointment: appointmentId
      ? ({
          appointmentId,
          appointmentPrice: values.price,
          providerCommissionType: values.providerCommissionType,
          isNoCharge: false,
          employeeId: values.employeeId || undefined,
          additionalServices: values.additionalServices?.map(s => ({
            serviceId: s.serviceId ?? undefined,
            employeeId: s.employeeId,
            providerCommissionType: s.providerCommissionType,
            price: s.price,
          })),
        } as NonNullable<InitiatePaymentPayload['appointment']>)
      : undefined,
    order: values.customOrderItems?.length
      ? {
          employeeId: values.employeeId || undefined,
          orderItems: values.customOrderItems.map(i => ({
            itemId: i.itemId,
            quantity: i.quantity,
            name: i.name,
            price: i.price,
          })),
          orderIdToDelete: values.failedOrderId,
        }
      : undefined,
    tipAmount: 0,
    paymentIntentId: values.paymentIntentId || failureData?.paymentIntentId,
    paymentMethod: values.paymentMethod as PaymentMethod,
  };

  if (values.paymentMethod === 'MANUAL') {
    data.paymentMethod = 'CREDIT';
    data.isManualCheckout = true;
  }

  if (values.paymentMethod === 'FREE') {
    data.paymentMethod = 'CASH';
    if (
      data.appointment &&
      !isFreeWithDiscount(values.paymentMethod, discount)
    ) {
      data.appointment.isNoCharge = true;
    }
  }

  if (
    data.appointment &&
    !data.appointment.isNoCharge &&
    values.price <= 0 &&
    values.customOrderItems?.length &&
    !values.additionalServices?.some(s => s.price > 0)
  ) {
    data.appointment.isNoCharge = true;
  }

  if (isTipAllowed(values.paymentMethod)) {
    if (values.tipPercentage != null) {
      data.tipPercentage = values.tipPercentage;
    } else if (values.tipAmt) {
      data.tipAmount = values.tipAmt;
    }
  }

  if (isDiscountAllowed(values.paymentMethod) && discount) {
    data.discountId = discount.id;
  }

  if (values.paymentMethod === 'GIFT_CARD') {
    data.giftCardType = guest ? 'external' : values.giftCardType || 'internal';
  }

  return { data, overridePaymentMethod };
}

export function checkPaymentType(
  payment: Pick<Payments | Payment, 'type' | 'types'> | null | undefined,
  type: PaymentType | PaymentType[],
) {
  if (!payment) return false;
  const paymentTypes = [payment.type, ...(payment.types ?? [])];
  if (Array.isArray(type)) {
    return type.some(t => paymentTypes.includes(t));
  }
  return paymentTypes.includes(type);
}

export type PaymentStatusKey = 'succeeded' | 'forfeited' | 'refunded';

export function getPaymentMethodPhrase(method: PaymentMethod) {
  if (CREDIT_PAYMENT_METHODS.includes(method)) return 'paid by card';
  if (method === PaymentMethod.GIFT_CARD) return 'paid by gift card';
  if (method === PaymentMethod.DIGITAL) return 'paid digitally';
  return 'paid in cash';
}

export function getPaymentStatusKey(
  payment:
    | Pick<Payments | Payment, 'type' | 'status' | 'depositStatus'>
    | null
    | undefined,
): PaymentStatusKey | null {
  if (payment?.type === 'DEPOSIT') {
    // depositStatus only means anything for a DEPOSIT payment — gate on
    // type before ever reading it.
    switch (payment.depositStatus) {
      case 'FORFEITED':
        return 'forfeited';
      case 'REFUNDED':
        return 'refunded';
      case 'USED':
      case 'UNUSED':
      case 'VOIDED':
      case null:
        break;
      default: {
        const exhaustive: never = payment.depositStatus;
        throw new Error(`Unhandled depositStatus: ${exhaustive}`);
      }
    }
  }

  if (payment?.status === 'SUCCESS') return 'succeeded';
  if (payment?.status === 'REFUNDED') return 'refunded';
  return null;
}

export function getPaymentsTipAmount(payments: (Payments | Payment)[]) {
  return (
    (payments?.reduce(
      (acc, payment) =>
        acc + (+(getMetadataFromPayment(payment).tip_amt ?? 0) || 0),
      0,
    ) || 0) / 100
  );
}

export function getPaymentsTotalDiscountedAmount(payments: Payments[]) {
  return (
    (payments?.reduce((acc, payment) => acc + payment.discountAmount, 0) || 0) /
    100
  );
}

export function getLineItemLabel(lineItem: Pick<PaymentLineItems, 'type'>) {
  switch (lineItem.type) {
    case 'SERVICE':
      return 'Appointment';
    case 'SERVICE_TAX':
      return 'Sales Tax';
    case 'SERVICE_RESCHEDULE':
      return 'Rescheduling Fee(s)';
    case 'SERVICE_RESCHEDULE_TAX':
      return 'Sales Tax';
    case 'RETAIL_ITEM':
      return 'Retail Item';
    case 'RETAIL_ITEM_TAX':
      return 'Sales Tax';
    case 'RETAIL_DISCOUNT':
      return 'Discount';
    case 'FEE':
      return 'Service Fee';
    case 'TIP':
      return 'Tip';
    case 'SERVICE_DISCOUNT':
      return 'Discount';
    default:
      return lineItem.type;
  }
}

function mergeLineItem(lineItems: LineItem[], label: string, amount: number) {
  const existing = lineItems.find(li => li.label === label);
  if (existing) {
    existing.amount += amount;
  } else {
    lineItems.push({ label, amount });
  }
}

// Fee slice to inflate a line item's display by for CD-style rendering.
// Reads `feeAmount` on CD payments; for legacy pre-CD under a CD-on business,
// decomposes bundled `FEE.amount` and splits fee-only across SERVICE/RETAIL.
function makeDisplayFeeResolver(
  payment: PaymentWithTransfersAndLineItems,
  businessCashDiscountEnabled: boolean,
): (li: Pick<PaymentLineItems, 'id' | 'feeAmount'>) => number {
  if (payment.cashDiscountEnabled) {
    return li => li.feeAmount ?? 0;
  }
  if (!businessCashDiscountEnabled) return () => 0;

  const feeLineTotal = payment.lineItems.reduce(
    (sum, li) => (li.type === 'FEE' ? sum + li.amount : sum),
    0,
  );
  const inflatableBase = payment.lineItems.reduce(
    (sum, li) =>
      li.type === 'SERVICE' || li.type === 'RETAIL_ITEM'
        ? sum + li.amount
        : sum,
    0,
  );
  if (feeLineTotal <= 0 || inflatableBase <= 0) return () => 0;

  const effRate = getPaymentEffectiveTaxRate(payment);
  const feeOnly =
    effRate > 0 ? Math.round(feeLineTotal / (1 + effRate)) : feeLineTotal;
  const feeByLineId = new Map<number, number>();
  payment.lineItems.forEach(li => {
    if (li.type === 'SERVICE' || li.type === 'RETAIL_ITEM') {
      feeByLineId.set(
        li.id,
        Math.round((feeOnly * li.amount) / inflatableBase),
      );
    }
  });
  return li => feeByLineId.get(li.id) ?? 0;
}

function resolveServiceLabel({
  lineItem,
  isDepositOnly,
  hasAdditionalServices,
  mainServiceName,
}: {
  lineItem: PaymentLineItemWithService;
  isDepositOnly: boolean;
  hasAdditionalServices: boolean;
  mainServiceName: string;
}): string {
  if (!isNil(lineItem.appointmentServiceId)) {
    return lineItem.appointmentService?.service?.serviceName ?? TATTOO_LABEL;
  }
  if (hasAdditionalServices) return mainServiceName;
  return isDepositOnly ? 'Deposit' : 'Appointment';
}

export function getPaymentLineItemsFromLineItems({
  payments,
  appointment = null,
  order,
  businessServices,
  businessCashDiscountEnabled = false,
}: {
  payments: PaymentWithTransfersAndLineItems[];
  appointment?: Appointment | Appointments | null;
  order: OrderWithItems | null;
  businessServices?: LoadedUser['business']['services'];
  /** When true, treat all payments as CD-inflated for display even when the
   * payment's own `cashDiscountEnabled` is false (legacy pre-CD payments
   * viewed under a now-CD business). */
  businessCashDiscountEnabled?: boolean;
}): LineItem[] {
  const lineItems: LineItem[] = [];
  const isDepositOnly = payments.every(p => checkPaymentType(p, 'DEPOSIT'));
  const mainServiceName = !isNil(appointment?.serviceId)
    ? businessServices?.find(s => s.serviceId === appointment?.serviceId)
        ?.serviceName ?? 'Service'
    : TATTOO_LABEL;
  const hasAdditionalServices =
    !!appointment &&
    payments.some(p => p.lineItems.some(li => !isNil(li.appointmentServiceId)));

  payments.forEach(payment => {
    const treatAsCd =
      payment.cashDiscountEnabled || businessCashDiscountEnabled;
    const getDisplayFee = makeDisplayFeeResolver(
      payment,
      businessCashDiscountEnabled,
    );

    // Retail discounts: allocate per order item so fully/partially discounted items show pre-discount price.
    // Under CD, feeAmount carries the fee-portion of the discount — include it
    // so the item line reads the pre-discount all-in sticker.
    const retailDiscountCents = payment.lineItems
      .filter(li => li.type === 'RETAIL_DISCOUNT')
      .reduce(
        (sum, li) => sum + li.amount + (treatAsCd ? li.feeAmount ?? 0 : 0),
        0,
      );
    if (retailDiscountCents > 0 && order?.orderItems?.length) {
      Object.entries(
        allocateRetailDiscountToOrderItems(order, retailDiscountCents),
      ).forEach(([orderItemIdStr, discountCents]) => {
        const orderItem = order.orderItems.find(
          oi => oi.orderItemId === parseInt(orderItemIdStr, 10),
        );
        if (orderItem && discountCents > 0) {
          mergeLineItem(
            lineItems,
            getRetailLineItemLabel(orderItem),
            discountCents / 100,
          );
        }
      });
    }

    payment.lineItems.forEach(lineItem => {
      const serviceLabel = resolveServiceLabel({
        lineItem,
        isDepositOnly,
        hasAdditionalServices,
        mainServiceName,
      });

      if (DISCOUNT_LINE_ITEM_TYPES.includes(lineItem.type)) {
        // Under CD, feeAmount carries the fee-portion of the deflated discount
        // so display shows the customer-facing inflated amount.
        const discountCents = treatAsCd
          ? lineItem.amount + (lineItem.feeAmount ?? 0)
          : lineItem.amount;
        if (lineItem.type === 'SERVICE_DISCOUNT') {
          mergeLineItem(lineItems, serviceLabel, discountCents / 100);
        }
        mergeLineItem(
          lineItems,
          getLineItemLabel(lineItem),
          -discountCents / 100,
        );
        return;
      }

      if (EXCLUDED_SUM_LINE_ITEM_TYPES.includes(lineItem.type)) return;

      let label =
        lineItem.type === 'SERVICE' ? serviceLabel : getLineItemLabel(lineItem);

      if (lineItem.type === 'RETAIL_ITEM' && !isNil(lineItem.orderItemId)) {
        const orderItem = order?.orderItems?.find(
          oi => oi.orderItemId === lineItem.orderItemId,
        );
        if (orderItem) label = getRetailLineItemLabel(orderItem);
      }

      // Credit CD lines carry their fee on `feeAmount`; legacy pre-CD lines
      // receive a synthetic slice decomposed from the FEE line item; feeless
      // CD lines are inflated separately below via cashDiscountApplied.
      // `getDisplayFee` returns 0 under non-CD, so no guard needed here.
      const displayAmountCents = lineItem.amount + getDisplayFee(lineItem);

      mergeLineItem(lineItems, label, displayAmountCents / 100);
    });
  });

  lineItems.sort(
    (a, b) =>
      LINE_ITEMS_ORDER.indexOf(a.label as (typeof LINE_ITEMS_ORDER)[number]) -
      LINE_ITEMS_ORDER.indexOf(b.label as (typeof LINE_ITEMS_ORDER)[number]),
  );

  // Sales Tax reflects actual tax paid; Cash Discount Applied shows only the
  // waived fee (tax-on-fee is neither charged nor surfaced as a deduction).
  const cashDiscountApplied = getCashDiscountAppliedAmount(payments);
  if (cashDiscountApplied > 0) {
    lineItems.push({
      label: CASH_DISCOUNT_APPLIED_LABEL,
      amount: -cashDiscountApplied,
    });
  }

  const voidedAmount = payments.reduce(
    (acc, p) =>
      p.status === 'VOIDED' ? acc + getPaymentDisplayAmount(p) : acc,
    0,
  );
  if (voidedAmount)
    lineItems.push({ label: 'Voided Amount', amount: -voidedAmount });

  const refundedAmount = payments.reduce(
    (acc, p) =>
      p.status === 'REFUNDED' ? acc + getPaymentDisplayAmount(p) : acc,
    0,
  );
  if (refundedAmount)
    lineItems.push({ label: 'Refunded Amount', amount: -refundedAmount });

  const total = lineItems.reduce((acc, item) => acc + item.amount, 0);
  lineItems.push({
    label: 'Total Paid',
    amount: Math.max(total, 0),
    isTotal: true,
  });

  return lineItems;
}

export function getPaymentLineItems({
  appointment,
  order,
  payments: passedPayments,
  businessServices,
  businessCashDiscountEnabled = false,
}: {
  appointment: Appointment | Appointments | null;
  order: OrderWithItems | null;
  payments: PaymentWithTransfersAndLineItems[];
  businessServices: LoadedUser['business']['services'];
  businessCashDiscountEnabled?: boolean;
}) {
  const lineItems: LineItem[] = [];

  const isSingleIncompletePayment =
    passedPayments.length === 1 && passedPayments[0]?.status === 'PENDING';
  if (isSingleIncompletePayment) {
    const singleIncompletePaymentAmount =
      getPaymentAmountWithoutServiceFeeOrSalesTax(passedPayments[0]) -
      (getMetadataFromPayment(passedPayments[0])?.tip_amt ?? 0) / 100;
    lineItems.push(
      {
        amount: singleIncompletePaymentAmount,
        label: 'Pending Total',
      },
      {
        amount: 0,
        label: 'Total Paid',
        isTotal: true,
      },
    );
    return lineItems;
  }

  const payments = passedPayments.filter(payment => isPaymentComplete(payment));
  const hasLineItems = payments.every(p => p.lineItems.length);

  if (hasLineItems) {
    return getPaymentLineItemsFromLineItems({
      payments,
      appointment,
      order,
      businessServices,
      businessCashDiscountEnabled,
    });
  }

  // if import payment is present, we do not have access to the `type` field
  const hasImportPayment = payments.some(p => checkPaymentType(p, 'IMPORT'));
  if (hasImportPayment) {
    lineItems.push({
      amount: payments.reduce(
        (acc, payment) =>
          acc +
          getPaymentAmountWithoutServiceFeeOrSalesTax(payment) -
          (getMetadataFromPayment(payment)?.tip_amt || 0) / 100,
        0,
      ),
      label: appointment ? 'Appointment Price' : 'Total Price',
    });
  } else {
    const hasApptDepositPayment = payments.some(p =>
      checkPaymentType(p, 'DEPOSIT'),
    );
    const hasApptFullPayment = payments.some(p => checkPaymentType(p, 'FULL'));
    const hasApptReschedulingFeePayment = payments.some(p =>
      checkPaymentType(p, 'RESCHEDULE'),
    );
    const hasApptPayment =
      !!appointment &&
      (hasApptDepositPayment ||
        hasApptFullPayment ||
        hasApptReschedulingFeePayment);

    if (hasApptPayment && appointment) {
      const isDepositPaidSeparately = isAppointmentDepositPaid(appointment);
      const refundedDeposit = payments.find(
        p => checkPaymentType(p, 'DEPOSIT') && p.status === 'REFUNDED',
      );

      if (hasApptDepositPayment && hasApptFullPayment) {
        lineItems.push({
          amount: appointment.price || 0,
          label: 'Appointment Price',
        });
      } else {
        if (hasApptDepositPayment) {
          const refundedMd = refundedDeposit
            ? getMetadataFromPayment(refundedDeposit)
            : null;
          const refundedDepositAmount =
            refundedMd && refundedDeposit
              ? getPaymentAmountWithoutServiceFeeOrSalesTax(refundedDeposit)
              : 0;
          lineItems.push({
            amount: appointment.depositPrice || refundedDepositAmount || 0,
            label: 'Deposit Price',
          });
        }
        if (hasApptFullPayment) {
          lineItems.push({
            amount:
              (appointment.price || 0) -
              (isDepositPaidSeparately ? appointment.depositPrice || 0 : 0),
            label: 'Appointment Price',
          });
        }
      }

      const reschedulePayments = payments.filter(p =>
        checkPaymentType(p, 'RESCHEDULE'),
      );
      const reschedulingFeesPaid = reschedulePayments?.length
        ? reschedulePayments.reduce(
            (acc, p) => acc + getPaymentAmountWithoutServiceFeeOrSalesTax(p),
            0,
          )
        : 0;
      if (reschedulingFeesPaid) {
        lineItems.push({
          amount: reschedulingFeesPaid,
          label: 'Rescheduling Fee(s) Paid',
        });
      }
    }

    if (order?.orderItems?.length) {
      order.orderItems.forEach(orderItem => {
        lineItems.push({
          amount:
            (orderItem.customItemPrice ?? orderItem.item?.price ?? 0) *
            orderItem.quantity,
          label: getRetailLineItemLabel(orderItem),
        });
      });
    }
  }

  const tipAmountPaid = getPaymentsTipAmount(payments);
  if (tipAmountPaid)
    lineItems.push({
      amount: tipAmountPaid,
      label: 'Tip ',
    });

  const taxesPaid = payments.reduce(
    (acc, payment) =>
      acc + Number(getMetadataFromPayment(payment)?.sales_tax || 0) / 100,
    0,
  );
  if (taxesPaid) {
    lineItems.push({
      amount: taxesPaid,
      label: 'Sales Tax',
    });
  }

  const discountedAmount = getPaymentsTotalDiscountedAmount(payments);
  if (discountedAmount > 0) {
    lineItems.push({
      amount: -discountedAmount,
      label: 'Discount',
    });
  }

  // if gift card is a part of the payment, subtract it from the total
  const giftCardAmountCents = getMetadataFromPayment(payments[0])
    ?.gift_card_amount_cents;
  const giftCardAmount =
    payments.length === 1 && giftCardAmountCents
      ? giftCardAmountCents / 100
      : 0;
  if (giftCardAmount) {
    lineItems.push({
      amount: -giftCardAmount,
      label: 'Gift Card',
    });
  }

  if (payments.length === 1 && payments[0]?.method === 'GIFT_CARD') {
    const totalAmount = lineItems.reduce((acc, item) => acc + item.amount, 0);
    const otherPaymentsAmount = totalAmount - payments[0].amount / 100;
    if (otherPaymentsAmount > 0) {
      lineItems.push({
        amount: -otherPaymentsAmount,
        label: 'Other Payments',
      });
    }
  }

  const refundedAmount = payments
    .filter(p => p.status === 'REFUNDED')
    .reduce((acc, payment) => {
      // if deposit was refunded prior to full payment, do not include in line item
      const isDepositBeforeFull =
        checkPaymentType(payment, 'DEPOSIT') &&
        !isAppointmentDepositPaid(appointment);
      return (
        acc +
        (isDepositBeforeFull ? 0 : getPaymentAmountWithoutServiceFee(payment))
      );
    }, 0);
  if (refundedAmount > 0) {
    lineItems.push({
      amount: -refundedAmount,
      label: 'Refunded Amount',
    });
  }

  const voidedAmount = payments
    .filter(p => p.status === 'VOIDED')
    .reduce(
      (acc, payment) => acc + getPaymentAmountWithoutServiceFee(payment),
      0,
    );
  if (voidedAmount) {
    lineItems.push({
      amount: -voidedAmount,
      label: 'Voided Amount',
    });
  }

  if (!lineItems.length) {
    lineItems.push({
      amount: 0,
      label: appointment ? 'Appointment Price' : 'Total Price',
    });
  }

  const totalPaid = payments
    .filter(p => p.status === 'SUCCESS')
    .reduce(
      (acc, payment) =>
        acc + getPaymentDisplayAmount(payment, businessCashDiscountEnabled),
      0,
    );
  lineItems.push({
    amount: totalPaid,
    label: 'Total Paid',
    isTotal: true,
  });

  return lineItems;
}

export function getSplitPaymentsAmountAlreadyAdded(
  paymentMethods: Pick<SplitPaymentMethod, 'amount'>[],
  currentIndex: number,
) {
  const otherPaymentMethods = paymentMethods.slice(0, currentIndex);
  return otherPaymentMethods.reduce((acc, curr) => acc + curr.amount, 0);
}

export function isFeelessPaymentMethod(
  method: PaymentMethod | CheckoutPaymentMethod | '' | null | undefined,
) {
  return (
    method === 'CASH' ||
    method === 'DIGITAL' ||
    method === 'GIFT_CARD' ||
    method === 'FREE'
  );
}

type SplitBucketsGroup = Pick<
  SplitPaymentsGroups,
  | 'quotedSubtotalCents'
  | 'quotedTaxCents'
  | 'tipAmountCents'
  | 'bundledFeeCents'
  | 'taxOnFeeCents'
>;

/**
 * Split `amount` cents across `buckets` in proportion to bucket size, using
 * largest-remainder rounding. Guarantees: each share ≤ its bucket, shares sum
 * to `min(amount, sum(buckets))`, no share is negative.
 */
function splitAcrossBuckets(amount: number, buckets: number[]): number[] {
  const total = buckets.reduce((sum, b) => sum + b, 0);
  if (total <= 0 || amount <= 0) return buckets.map(() => 0);
  const clamped = Math.min(amount, total);
  const raw = buckets.map(b => (clamped * b) / total);
  const shares = raw.map(Math.floor);
  let residual = clamped - shares.reduce((sum, s) => sum + s, 0);
  const order = raw
    .map((r, i) => ({ frac: r - Math.floor(r), i }))
    .sort((a, b) => b.frac - a.frac);
  for (let k = 0; k < order.length && residual > 0; k += 1) {
    const { i } = order[k];
    if (shares[i] < buckets[i]) {
      shares[i] += 1;
      residual -= 1;
    }
  }
  return shares;
}

/**
 * Buckets remaining after every payment strictly before `currentIndex`.
 * `paymentMethods[i].amount` is the customer-facing share (all-in coverage,
 * inflated by any waived fee for feeless CD payments), so each prior payment
 * consumes deflated + fee + taxOnFee proportionally — same math for cards and
 * feeless payments once you're working in share-space.
 */
export function getSplitBucketsRemaining(
  group: SplitBucketsGroup | null | undefined,
  paymentMethods: Pick<SplitPaymentMethod, 'amount' | 'paymentMethod'>[],
  currentIndex: number,
) {
  // Split subtotal/tax/tip as separate buckets to match backend's
  // proportionalSplit granularity — lumping them would shift rounding
  // remainders and drift the max face by 1 cent.
  let subtotalRem = group?.quotedSubtotalCents ?? 0;
  let taxRem = group?.quotedTaxCents ?? 0;
  let tipRem = group?.tipAmountCents ?? 0;
  let feeRem = group?.bundledFeeCents ?? 0;
  let taxOnFeeRem = group?.taxOnFeeCents ?? 0;

  for (let i = 0; i < currentIndex; i += 1) {
    const prev = paymentMethods[i];
    if (!prev) continue;
    const shareCents = Math.round(prev.amount * 100);
    const totalRem = subtotalRem + taxRem + tipRem + feeRem + taxOnFeeRem;
    if (totalRem <= 0) continue;
    const capped = Math.min(shareCents, totalRem);
    const [subtotalShare, taxShare, tipShare, feeShare, taxOnFeeShare] =
      splitAcrossBuckets(capped, [
        subtotalRem,
        taxRem,
        tipRem,
        feeRem,
        taxOnFeeRem,
      ]);
    subtotalRem -= subtotalShare;
    taxRem -= taxShare;
    tipRem -= tipShare;
    feeRem -= feeShare;
    taxOnFeeRem -= taxOnFeeShare;
  }

  return { deflatedRem: subtotalRem + taxRem + tipRem, feeRem, taxOnFeeRem };
}

/**
 * Customer-facing coverage a not-yet-submitted split payment would provide.
 * Card / non-CD → face value. Feeless under CD → face capped at deflated
 * remaining, plus the fee slice that face waives (proportional to the deflated
 * remaining after prior payments' shares are subtracted).
 */
export function projectSplitPaymentEffectiveCents({
  amountCents,
  method,
  group,
  paymentMethods,
  currentIndex,
  cashDiscountEnabled,
}: {
  amountCents: number;
  method: PaymentMethod | CheckoutPaymentMethod | '' | null | undefined;
  group: SplitBucketsGroup | null | undefined;
  paymentMethods: Pick<SplitPaymentMethod, 'amount' | 'paymentMethod'>[];
  currentIndex: number;
  cashDiscountEnabled: boolean;
}): { effectiveAmountCents: number; isFeelessUnderCd: boolean } {
  const isFeelessUnderCd =
    cashDiscountEnabled && isFeelessPaymentMethod(method);
  if (!isFeelessUnderCd) {
    return { effectiveAmountCents: amountCents, isFeelessUnderCd };
  }
  const { deflatedRem, feeRem, taxOnFeeRem } = getSplitBucketsRemaining(
    group,
    paymentMethods,
    currentIndex,
  );
  if (deflatedRem <= 0) {
    return { effectiveAmountCents: amountCents, isFeelessUnderCd };
  }
  const capped = Math.min(amountCents, deflatedRem);
  const waived = Math.round(((feeRem + taxOnFeeRem) * capped) / deflatedRem);
  return {
    effectiveAmountCents: capped + waived,
    isFeelessUnderCd,
  };
}

export function getSplitPaymentsForm(
  totalPriceCents: number,
  paymentMethods: Pick<SplitPaymentMethod, 'amount'>[],
  currentIndex: number,
): SplitPaymentForm {
  const amountAlreadyAdded = getSplitPaymentsAmountAlreadyAdded(
    paymentMethods,
    currentIndex,
  );
  const remainingAmount =
    Math.round(totalPriceCents - amountAlreadyAdded * 100) / 100;

  return {
    amount:
      amountAlreadyAdded && remainingAmount
        ? formatDollarAmount(remainingAmount, true, true).replace(/,/g, '')
        : '',
    paymentMethod: '' as CheckoutPaymentMethod,
    giftCardType: 'internal',
  };
}

export function getCheckoutErrorMessage(data: FailureData) {
  let failureCode: string | string[] = data.failureCode?.split('_') || [];
  failureCode = (failureCode as string[]).map((word, index) => {
    if (index === 0) {
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    }
    return word.toLowerCase();
  });
  failureCode = failureCode.join(' ');
  const tempFailureMessage = `Error message: ${failureCode}. ${data.failureMessage}`;
  return tempFailureMessage;
}

export function isTipDisplayed({
  values,
  appointment,
  user,
}: {
  values: CheckoutFormValues;
  user: LoadedUser | null;
  appointment?: Pick<Appointment, 'appointmentId'> | null;
}) {
  const isCreditCardPayment = isCardPaymentMethod(values.paymentMethod);
  const disableCreditTipping =
    user?.business.disableCreditTipping && isCreditCardPayment;

  return (
    (!values.paymentMethod || isTipAllowed(values.paymentMethod)) &&
    (!!appointment || values.employeeId) &&
    !disableCreditTipping
  );
}

export function getPaymentMethodText(paymentMethod: PaymentMethod) {
  const displayPaymentMethod =
    paymentMethod === 'TERMINAL' ? 'CREDIT' : paymentMethod;
  return convertSnakeToTitleCase(displayPaymentMethod);
}

export function getPaymentMethodFee(
  paymentMethod: CheckoutPaymentMethod,
  business: LoadedUser['business'] | null | undefined,
) {
  const methodFeeRate =
    (paymentMethod === 'TERMINAL'
      ? business?.inStoreCustomerServiceFeeRate
      : paymentMethod === 'CREDIT' || paymentMethod === 'MANUAL'
      ? business?.onlineCustomerServiceFeeRate
      : paymentMethod === 'CASH' ||
        paymentMethod === 'GIFT_CARD' ||
        paymentMethod === 'FREE' ||
        paymentMethod === 'DIGITAL'
      ? 0
      : DEFAULT_CUSTOMER_SERVICE_FEE) ?? DEFAULT_CUSTOMER_SERVICE_FEE;

  return methodFeeRate;
}

export function calculateFeeAmount(
  totalAmount: number,
  feeRate: number,
  paymentMethod: PaymentMethod | CheckoutPaymentMethod,
) {
  const min =
    feeRate <= 0
      ? 0
      : paymentMethod === 'TERMINAL'
      ? TERMINAL_MIN_FEE_CENTS / 100
      : ONLINE_MIN_FEE_CENTS / 100;
  return Math.max(min, totalAmount * feeRate);
}

export function isCashOrDigitalMethod(
  paymentMethod: PaymentMethod | CheckoutPaymentMethod | null | undefined,
) {
  return (
    paymentMethod === PaymentMethod.CASH ||
    paymentMethod === PaymentMethod.DIGITAL
  );
}

export const INSTANT_PAYOUT_LIMITED_TITLE = 'Instant Payouts are limited';
export const INSTANT_PAYOUT_LIMITED_SUBTITLE =
  "Instant Payouts are limited during the early stages of an account to help prevent fraud. Any funds you're not able to withdraw via instant payouts will automatically be sent through our standard payouts, which take 2-3 business days and have no fee. As your account builds up appointment history, your instant payout limits will quickly increase.";
export const INSTANT_PAYOUT_DAILY_EXHAUSTED_TITLE =
  "You've reached your daily instant payout limit";

export function getBnplNeedsStripeDashboard(
  capabilities: PaymentsCapabilities,
) {
  return Object.values(capabilities).some(capability => {
    if (capability.active) return false;
    const reason = capability.inactiveReason;
    return (
      reason !== 'rejected.inactivity' &&
      reason !== 'rejected.unsupported_business' &&
      reason !== 'rejected.other' &&
      reason !== 'platform_disabled' &&
      reason !== 'platform_paused'
    );
  });
}

export function getBnplDisabledReason(capabilities: PaymentsCapabilities) {
  const reasonsText = new Array<string>();

  Object.entries(capabilities).forEach(([type, capability]) => {
    if (capability.active) return;

    const reason = capability.inactiveReason;

    switch (reason) {
      case 'rejected.inactivity':
      case 'rejected.unsupported_business':
      case 'rejected.other':
      case 'platform_disabled':
      case 'platform_paused':
        reasonsText.push(
          `• Your account is not eligible for ${capitalize(
            type,
          )}. Please contact support for assistance.`,
        );
        break;
      default:
        reasonsText.push(
          `• Please head to your Stripe dashboard to complete the requirements and enable ${capitalize(
            type,
          )}`,
        );
        break;
    }
  });

  return reasonsText;
}

/**
 * Returns the service-specific commission rate for an employee, or null if the
 * employee is not commission-based or has no custom rate set for the service.
 */
export function getEmployeeServiceCommissionRate(
  employees: LoadedUser['business']['employees'],
  services: LoadedUser['business']['services'],
  employeeId: string | null | undefined,
  serviceId: number | null | undefined,
): number | null {
  if (!employeeId || isNil(serviceId)) return null;
  const isCommissionEmployee =
    employees.find(e => e.employeeId === employeeId)?.payrollType ===
    'COMMISSION';
  if (!isCommissionEmployee) return null;
  const ets = services
    ?.find(s => s.serviceId === serviceId)
    ?.employeesToServices?.find(e => e.employeeId === employeeId);
  return ets?.serviceCommissionRate != null && ets.serviceCommissionRate >= 0
    ? ets.serviceCommissionRate
    : null;
}

/**
 * Returns the default ProviderCommissionType for an employee/service combination.
 * SERVICE if a valid service-specific commission rate exists, BASE otherwise.
 */
export function getDefaultProviderCommissionType(
  serviceCommissionRate: number | null | undefined,
): ProviderCommissionType {
  return serviceCommissionRate != null && serviceCommissionRate >= 0
    ? ProviderCommissionType.SERVICE
    : ProviderCommissionType.BASE;
}

/**
 * Returns the commission rate options available for an employee/service pair.
 * Rates are 0–1 fractions (e.g. 0.6 = 60%).
 */
export function getProviderCommissionOptions(
  employees: LoadedUser['business']['employees'],
  services: LoadedUser['business']['services'],
  employeeId: string | null | undefined,
  serviceId: number | null | undefined,
): ProviderCommissionOption[] {
  if (!employeeId) return [];
  const employee = employees.find(e => e.employeeId === employeeId);
  if (!employee) return [];

  const etsRate = getEmployeeServiceCommissionRate(
    employees,
    services,
    employeeId,
    serviceId,
  );
  const baseRate = employee.paymentToEmployeePercent ?? 0;
  const options: ProviderCommissionOption[] = [];

  if (!isNil(etsRate)) {
    options.push({ type: ProviderCommissionType.SERVICE, rate: etsRate! });
  }

  options.push({ type: ProviderCommissionType.BASE, rate: baseRate });

  if (
    employee.payrollType === 'COMMISSION' &&
    employee.bonusCommissionPercent
  ) {
    const bonusRate = Math.min(baseRate + employee.bonusCommissionPercent, 1);
    options.push({ type: ProviderCommissionType.BONUS, rate: bonusRate });
  }

  return options;
}

export function getGroupedTransfers(payments: PaymentWithTransfers[]) {
  const transfers = payments
    .flatMap(payment => payment.transfers ?? [])
    .sort(
      (a, b) =>
        new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime(),
    );

  return Object.values(
    transfers.reduce(
      (acc, transfer) => {
        const key =
          transfer.entity === 'EMPLOYEE'
            ? transfer.stripeAccountId ?? 'EMPLOYEE'
            : 'SHOP';
        if (!acc[key]) {
          acc[key] = { ...transfer, amount: 0 };
        }
        acc[key].amount += transfer.amount;
        if (new Date(transfer.updatedAt) > new Date(acc[key].updatedAt)) {
          acc[key].updatedAt = transfer.updatedAt;
          acc[key].status = transfer.status;
        }
        return acc;
      },
      {} as Record<string, (typeof transfers)[0]>,
    ),
  );
}
